import { Typography } from '@material-ui/core';
import { IList, tuple } from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { viewTexts } from '../../../constants/view-texts';
import { IImageInfoType } from '../../../types/record/image-info';
import { ImageThumbnailGridCell } from '../../shared/image-thumbnail/image-thumbnail-grid-cell';
import { MessageArea } from '../../shared/message-area';
import { LoadingButtonState } from '../../shared/mui/loading-button-state';
import { MuiSwitch } from '../../shared/mui/mui-switch';
import { Root } from '../../shared/styled/root';
import { DndFileUploadArea } from './dnd-file-upload-area/dnd-file-upload-area';

const vt = viewTexts.pages.modelImageList;

const DropzoneWrapper = styled.div`
  padding: 10px;
`;

const Switchs = styled.div`
  display: flex;
  align-items: center;
`;

const FilterSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
  margin-right: 20px;
`;

const BulkDeleteSwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 5px;
`;

const ImagePreviewWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

const MessageAreaWrapper = styled.div`
  padding: 20px 0;
`;

const AnnotatedRatio = styled.div`
  padding: 5px;
`;

interface Props {
  imageList: IList<IImageInfoType>;
  imageListFiltered: IList<IImageInfoType>;
  onImageClick: (imgId: number) => void;
  onDeleteClick: (imgId: number) => void;
  annotatedIsVisible: boolean;
  showAll: () => void;
  hideAnnotated: () => void;
  IsBulkDelete: boolean;
  InstantDelete: () => void;
  ConfirmDelete: () => void;
  messageArea: {
    status: 'default' | 'warning' | 'alert';
    message: readonly string[];
    showStartTrainingButton: boolean;
  };
  startTrainingButtonLabel: string;
  startTrainingButtonIsDisabled: boolean;
  startTrainingButtonStatus: LoadingButtonState['status'];
  onStartTrainingClick: () => void;
}

export const ModelImageListView: FC<Props> = memo(
  ({
    imageList,
    imageListFiltered,
    onImageClick,
    onDeleteClick,
    annotatedIsVisible,
    showAll,
    hideAnnotated,
    IsBulkDelete,
    InstantDelete,
    ConfirmDelete,
    messageArea,
    startTrainingButtonLabel,
    startTrainingButtonIsDisabled,
    startTrainingButtonStatus,
    onStartTrainingClick
  }: Readonly<Props>) => {
    const imagesWithHandler = useMemo(
      () =>
        imageListFiltered.map(img =>
          tuple(
            img,
            () => onImageClick(img.id),
            () => onDeleteClick(img.id)
          )
        ),
      [imageListFiltered, onImageClick, onDeleteClick]
    );

    const filterSwitchHandler = useCallback(
      (checked: boolean) => {
        if (checked) {
          hideAnnotated();
        } else {
          showAll();
        }
      },
      [showAll, hideAnnotated]
    );

    const bulkDeleteSwitchHandler = useCallback(
      (checked: boolean) => {
        if (checked) {
          InstantDelete();
        } else {
          ConfirmDelete();
        }
      },
      [InstantDelete, ConfirmDelete]
    );

    const numAnnotated = useMemo<number>(
      () => imageList.filter(e => e.isAnnotated).size,
      [imageList]
    );

    return (
      <Root>
        {messageArea.message.length !== 0 ? (
          <MessageAreaWrapper>
            <MessageArea
              status={messageArea.status}
              messages={messageArea.message}
              showButton={messageArea.showStartTrainingButton}
              buttonLabel={startTrainingButtonLabel}
              buttonIsDisabled={startTrainingButtonIsDisabled}
              buttonStatus={startTrainingButtonStatus}
              onButtonClick={onStartTrainingClick}
            />
          </MessageAreaWrapper>
        ) : (
          undefined
        )}
        <AnnotatedRatio>
          <Typography>
            {vt.numAnnotated(imageList.size, numAnnotated)}
          </Typography>
        </AnnotatedRatio>
        <DropzoneWrapper>
          <DndFileUploadArea />
        </DropzoneWrapper>
        <Switchs>
          <FilterSwitchWrapper>
            <span>{vt.showAll}</span>
            <MuiSwitch
              color='default'
              checked={!annotatedIsVisible}
              onChange={filterSwitchHandler}
            />
            <span>{vt.hideAnnotated}</span>
          </FilterSwitchWrapper>
          <BulkDeleteSwitchWrapper>
            <span>{vt.isBuklDelete}</span>
            <MuiSwitch
              color='secondary'
              checked={IsBulkDelete}
              onChange={bulkDeleteSwitchHandler}
            />
          </BulkDeleteSwitchWrapper>
        </Switchs>
        <ImagePreviewWrapper>
          {imagesWithHandler.map(
            ([img, onImageClickHandler, onDeleteClickHandler]) => (
              <ImageThumbnailGridCell
                key={img.id}
                image={img}
                chipLabel={vt.isAnnotated}
                buttonLabel={IsBulkDelete ? viewTexts.others.instantDelete : viewTexts.others.delete}
                onImageClick={onImageClickHandler}
                onDeleteClick={onDeleteClickHandler}
              />
            )
          )}
        </ImagePreviewWrapper>
      </Root>
    );
  }
);

ModelImageListView.displayName = 'ModelImageListView';
